/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import { getConnectReportsLookupData } from "../../services/ConnectReports";
import { Modal, Button, Form } from "react-bootstrap";

// number of metrics selections to display in the modal
const METRIC_SLOT_COUNT = 12;

/**
 * Used to configure the time span of the metrics data trend and to
 * select a metric to display for each available slot
 * @param {Object} params
 * 	- show: is the modal shown or not
 * 	- onHide: function to call on modal dismiss on cancel
 * 	- metricsConfig: existing configuration
 * 	- updateMetricsConfig: function to call on save
 */
const MetricConfigModal = ({ show, onHide, metricsConfig, updateMetricsConfig }) => {
	const [lookupData, setLookupData] = useState([]);

	const [metrics, setMetrics] = useState(metricsConfig.selectedMetrics);
	const [trendLengthMinutes, setTrendLengthMinutes] = useState(metricsConfig.trendLengthMinutes);

	useEffect(() => {
		// reset metrics on modal show
		if (show) {
			const metricsCount = metricsConfig?.selectedMetrics?.length || 0;
			const differenceFromMax = METRIC_SLOT_COUNT - metricsCount;

			// we want to show the correct number of config slots to select from,
			// if the current count of metrics is less than METRIC_SLOT_COUNT,
			// we can fill in the remaining slots with ""
			const additionalConfigs = Array(differenceFromMax)
				.fill()
				.map((_) => "");

			setMetrics([...metricsConfig.selectedMetrics, ...additionalConfigs]);
			setTrendLengthMinutes(metricsConfig.trendLengthMinutes);
		}
	}, [show, metricsConfig]);

	useEffect(() => {
		const fetchLookupData = async () => {
			const lookupDataResponse = await getConnectReportsLookupData();

			setLookupData(lookupDataResponse);
		};

		fetchLookupData();
	}, []);

	const metricDescription = (metricName) => {
		const metric = lookupData.find((x) => x.id === metricName);
		return metric ? metric.description : "";
	};

	return (
		<Modal show={show} scrollable onHide={onHide} backdrop="static" size="lg" centered>
			<Modal.Header closeButton>
				<Modal.Title>Configure Queue Metrics</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ height: 400 }}>
				<div className="px-4 py-2">
					<Form>
						<Form.Group controlId="formBasicRange">
							<Form.Label>
								Show metrics trend for the last{" "}
								<span className="font-weight-bold">
									{trendLengthMinutes === 1 ? `minute` : `${trendLengthMinutes} minutes`}
								</span>
							</Form.Label>
							<Form.Control
								type="range"
								min="1"
								max="10"
								step="1"
								value={trendLengthMinutes}
								onChange={(e) => setTrendLengthMinutes(Number(e.target.value))}
							/>
						</Form.Group>
					</Form>
					{metrics.map((m, i) => (
						<div key={i}>
							<Form.Group controlId="formBasicEmail">
								<Form.Label>{`Metric slot ${i + 1}`}</Form.Label>
								<Form.Control
									as="select"
									size="sm"
									value={m}
									onChange={(e) => {
										e.persist();
										setMetrics((c) => [...c.slice(0, i), e.target.value, ...c.slice(i + 1)]);
									}}
								>
									<option value=""></option>
									{lookupData.map((x) => (
										<option key={x.id} value={x.id}>
											{x.label}
										</option>
									))}
								</Form.Control>
								<Form.Text className="text-muted">{metricDescription(m)}</Form.Text>
							</Form.Group>
						</div>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="sm" variant={"outline-secondary"} onClick={() => onHide()}>
					Cancel
				</Button>
				<Button
					size="sm"
					variant={"outline-secondary"}
					onClick={() => updateMetricsConfig({ selectedMetrics: metrics, trendLengthMinutes })}
				>
					Save
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default MetricConfigModal;
